import { useState, useCallback, useEffect, useRef } from 'react'
import { useSupabase } from '@/lib/supabase-context'
import { searchService, type SearchResult, type SearchResultAgrupado } from '../services/searchService'
import { useOrganizacion } from '../providers/OrganizacionProvider'

// Re-export types
export type { SearchResult, SearchResultAgrupado } from '../services/searchService'

interface UseGlobalSearchOptions {
  debounceMs?: number
  limit?: number
  workspace?: string
  agrupado?: boolean
}

interface UseGlobalSearchReturn {
  query: string
  setQuery: (query: string) => void
  resultados: SearchResult[]
  resultadosAgrupados: SearchResultAgrupado[]
  isLoading: boolean
  error: Error | null
  buscar: (q?: string) => Promise<void>
  limpiar: () => void
  obtenerRuta: (resultado: SearchResult) => string
}

export function useGlobalSearch(options: UseGlobalSearchOptions = {}): UseGlobalSearchReturn {
  const {
    debounceMs = 300,
    limit = 20,
    workspace,
    agrupado = true,
  } = options

  const { supabase } = useSupabase()
  const { organizacion } = useOrganizacion()
  const [query, setQuery] = useState('')
  const [resultados, setResultados] = useState<SearchResult[]>([])
  const [resultadosAgrupados, setResultadosAgrupados] = useState<SearchResultAgrupado[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const buscar = useCallback(async (q?: string) => {
    const searchQuery = q ?? query

    if (!searchQuery.trim() || !organizacion?.id || !supabase) {
      setResultados([])
      setResultadosAgrupados([])
      return
    }

    // Cancelar búsqueda anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    setError(null)

    try {
      if (agrupado) {
        const results = await searchService.buscarAgrupado(
          supabase,
          searchQuery,
          organizacion.id,
          workspace,
          limit
        )
        setResultadosAgrupados(results)
        // Flatten para resultados simples
        setResultados(results.flatMap(g => g.resultados))
      } else {
        const results = await searchService.buscar(
          supabase,
          searchQuery,
          organizacion.id,
          workspace,
          limit
        )
        setResultados(results)
        setResultadosAgrupados([])
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError(err)
        console.error('Error en búsqueda:', err)
      }
    } finally {
      setIsLoading(false)
    }
  }, [query, organizacion?.id, supabase, workspace, limit, agrupado])

  // Debounce de búsqueda cuando cambia query
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (!query.trim()) {
      setResultados([])
      setResultadosAgrupados([])
      return
    }

    timeoutRef.current = setTimeout(() => {
      buscar(query)
    }, debounceMs)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [query, debounceMs, buscar])

  const limpiar = useCallback(() => {
    setQuery('')
    setResultados([])
    setResultadosAgrupados([])
    setError(null)
  }, [])

  const obtenerRuta = useCallback((resultado: SearchResult) => {
    return searchService.obtenerRuta(resultado)
  }, [])

  return {
    query,
    setQuery,
    resultados,
    resultadosAgrupados,
    isLoading,
    error,
    buscar,
    limpiar,
    obtenerRuta,
  }
}
